import React, { useState, useEffect } from 'react';
import { FolderIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import Modal from '../common/Modal';

export default function MoveFolderModal({ isOpen, onClose, onMove, currentProjectId }) {
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadFolders();
    }
  }, [isOpen, currentFolder]);

  const loadFolders = async () => {
    try {
      let query = supabase
        .from('data_folders')
        .select('*')
        .eq('project_id', currentProjectId);

      if (currentFolder) {
        query = query.eq('parent_id', currentFolder.id);
      } else {
        query = query.is('parent_id', null);
      }

      const { data, error } = await query;
      if (error) throw error;
      setFolders(data || []);
    } catch (error) {
      console.error('Error loading folders:', error);
    }
  };

  const handleFolderClick = (folder) => {
    setCurrentFolder(folder);
    setBreadcrumbs([...breadcrumbs, folder]);
  };

  const navigateUp = () => {
    if (breadcrumbs.length > 0) {
      const newBreadcrumbs = breadcrumbs.slice(0, -1);
      setBreadcrumbs(newBreadcrumbs);
      setCurrentFolder(newBreadcrumbs[newBreadcrumbs.length - 1] || null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Move to Folder"
      showConfirm={false}
    >
      <div className="space-y-4">
        {/* Current Location Section */}
        <div className="bg-dark-300 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <button
                onClick={() => {
                  setCurrentFolder(null);
                  setBreadcrumbs([]);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                Home
              </button>
              {breadcrumbs.map((folder, index) => (
                <React.Fragment key={folder.id}>
                  <ChevronRightIcon className="w-4 h-4 text-gray-500" />
                  <button
                    onClick={() => {
                      setCurrentFolder(folder);
                      setBreadcrumbs(breadcrumbs.slice(0, index + 1));
                    }}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {folder.name}
                  </button>
                </React.Fragment>
              ))}
            </div>
            <button
              onClick={() => {
                onMove(currentFolder?.id || null);
                onClose();
              }}
              className="px-3 py-1 bg-accent-primary text-white text-sm rounded hover:bg-accent-primary/90 transition-colors"
            >
              Move Here
            </button>
          </div>
        </div>

        {/* Folders List */}
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {folders.map((folder) => (
            <div
              key={folder.id}
              className="flex items-center justify-between p-3 rounded-lg bg-dark-200 hover:bg-dark-300 transition-colors group"
            >
              <button
                onClick={() => handleFolderClick(folder)}
                className="flex items-center gap-3 flex-1 text-left"
              >
                <FolderIcon className="w-5 h-5 text-gray-400" />
                <span>{folder.name}</span>
              </button>
              <button
                onClick={() => {
                  onMove(folder.id);
                  onClose();
                }}
                className="px-3 py-1 bg-accent-primary text-white text-sm rounded opacity-0 group-hover:opacity-100 hover:bg-accent-primary/90 transition-all"
              >
                Move Here
              </button>
            </div>
          ))}
          {folders.length === 0 && (
            <div className="text-center text-gray-400 py-4">
              No folders found
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
} 